// Vendors.
import React, { useEffect, useReducer } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import makeStyles from "@mui/styles/makeStyles";

// Custom.
import AuthGuard from "pages/auth/AuthGuard";
import ErrorBoundary from "pages/auth/ErrorBoundary";
import routes from "pages/routes";
import { VoAppContext, reducer, initialState } from "services/GlobalState";
import { ProtectedRoute } from "@vocollege/components";
import {
  stylesCommon,
  stylesToast,
  stylesLayout,
  stylesGlobal,
} from "@vocollege/theme";
import Header from "containers/Layout/Header";
import VoLoader from "@vocollege/components/dist/VoLoader";
// import { localStorage } from "@vocollege/app/dist/modules/VoHelpers";
import VoGroups from "@vocollege/app/dist/modules/VoGroups";
import VoAuth from "@vocollege/app/dist/modules/VoAuth";

const useStylesCommon = makeStyles(() => stylesCommon);
const useStylesToast = makeStyles(() => stylesToast);
const useStylesLayout = makeStyles(() => stylesLayout);
const useStylesGlobal = makeStyles(() => stylesGlobal);

function App() {
  const [state, dispatch] = useReducer(reducer, initialState);
  useStylesCommon();
  useStylesToast();
  useStylesLayout();
  useStylesGlobal();

  useEffect(() => {
    let group = VoGroups.getCurrent();
    if (group) {
      dispatch({ key: "group", values: group });
    }
  }, []);

  return (
    <BrowserRouter>
      <React.Suspense fallback={<VoLoader />}>
        <ErrorBoundary>
          <VoAppContext.Provider value={{ state, dispatch }}>
            <AuthGuard>
              {!state.hideHeader && VoAuth.currentUser && <Header />}
              <Routes>
                <Route path={routes.auth.path} />
                <Route
                  path={routes.login.path}
                  element={<routes.login.component />}
                />
                <Route
                  path={routes.session.path}
                  element={<routes.session.component />}
                />
                <Route path={routes["accounts.retention"].path} element={
                  <ProtectedRoute path={routes["accounts.retention"].path}
                    component={routes["accounts.retention"].component}
                    abilities={routes["accounts.retention"].abilities} />
                } />
                <Route
                  path={routes.users.path}
                  element={
                    <ProtectedRoute
                      path={routes.users.path}
                      component={routes.users.component}
                      abilities={routes.users.abilities}
                    />
                  }
                />
                <Route
                  path={routes["users.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["users.id"].path}
                      component={routes["users.id"].component}
                      abilities={routes["users.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["acl.roles.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["acl.roles.id"].path}
                      component={routes["acl.roles.id"].component}
                      abilities={routes["acl.roles.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["acl.roles"].path}
                  element={
                    <ProtectedRoute
                      path={routes["acl.roles"].path}
                      component={routes["acl.roles"].component}
                      abilities={routes["acl.roles"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["acl.permissions"].path}
                  element={
                    <ProtectedRoute
                      path={routes["acl.permissions"].path}
                      component={routes["acl.permissions"].component}
                      abilities={routes["acl.permissions"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.articles"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.articles"].path}
                      component={routes["content.articles"].component}
                      abilities={routes["content.articles"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.articles.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.articles.id"].path}
                      component={routes["content.articles.id"].component}
                      abilities={routes["content.articles.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.applications"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.applications"].path}
                      component={routes["content.applications"].component}
                      abilities={routes["content.applications"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.applications.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.applications.id"].path}
                      component={routes["content.applications.id"].component}
                      abilities={routes["content.applications.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.pages"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.pages"].path}
                      component={routes["content.pages"].component}
                      abilities={routes["content.pages"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.pages.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.pages.id"].path}
                      component={routes["content.pages.id"].component}
                      abilities={routes["content.pages.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.books"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.books"].path}
                      component={routes["content.books"].component}
                      abilities={routes["content.books"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.books.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.books.id"].path}
                      component={routes["content.books.id"].component}
                      abilities={routes["content.books.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.sections"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.sections"].path}
                      component={routes["content.sections"].component}
                      abilities={routes["content.sections"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.sections.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.sections.id"].path}
                      component={routes["content.sections.id"].component}
                      abilities={routes["content.sections.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.events"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.events"].path}
                      component={routes["content.events"].component}
                      abilities={routes["content.events"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.events.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.events.id"].path}
                      component={routes["content.events.id"].component}
                      abilities={routes["content.events.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.cards"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.cards"].path}
                      component={routes["content.cards"].component}
                      abilities={routes["content.cards"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.cards.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.cards.id"].path}
                      component={routes["content.cards.id"].component}
                      abilities={routes["content.cards.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.queues"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.queues"].path}
                      component={routes["content.queues"].component}
                      abilities={routes["content.queues"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.queues.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.queues.id"].path}
                      component={routes["content.queues.id"].component}
                      abilities={routes["content.queues.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.educations"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.educations"].path}
                      component={routes["content.educations"].component}
                      abilities={routes["content.educations"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["content.educations.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["content.educations.id"].path}
                      component={routes["content.educations.id"].component}
                      abilities={routes["content.educations.id"].abilities}
                    />
                  }
                />

                <Route
                  path={routes.groups.path}
                  element={
                    <ProtectedRoute
                      path={routes.groups.path}
                      component={routes.groups.component}
                      abilities={routes.groups.abilities}
                    />
                  }
                />
                <Route
                  path={routes["groups.roles.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["groups.roles.id"].path}
                      component={routes["groups.roles.id"].component}
                      abilities={routes["groups.roles.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["groups.roles"].path}
                  element={
                    <ProtectedRoute
                      path={routes["groups.roles"].path}
                      component={routes["groups.roles"].component}
                      abilities={routes["groups.roles"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["groups.permissions"].path}
                  element={
                    <ProtectedRoute
                      path={routes["groups.permissions"].path}
                      component={routes["groups.permissions"].component}
                      abilities={routes["groups.roles"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["groups.id"].path}
                  element={
                    <ProtectedRoute
                      path={routes["groups.id"].path}
                      component={routes["groups.id"].component}
                      abilities={routes["groups.id"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["navigation.menus"].path}
                  element={
                    <ProtectedRoute
                      path={routes["navigation.menus"].path}
                      component={routes["navigation.menus"].component}
                      abilities={routes["navigation.menus"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["navigation.menus.name"].path}
                  element={
                    <ProtectedRoute
                      path={routes["navigation.menus.name"].path}
                      component={routes["navigation.menus.name"].component}
                      abilities={routes["navigation.menus.name"].abilities}
                    />
                  }
                />
                <Route
                  path={routes.docs.path}
                  element={
                    <ProtectedRoute
                      path={routes.docs.path}
                      component={routes.docs.component}
                      abilities={routes.docs.abilities}
                    />
                  }
                />
                <Route
                  path={routes["docs.portfolio"].path}
                  element={
                    <ProtectedRoute
                      path={routes["docs.portfolio"].path}
                      component={routes["docs.portfolio"].component}
                      abilities={routes["docs.portfolio"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["docs.folder"].path}
                  element={
                    <ProtectedRoute
                      path={routes["docs.folder"].path}
                      component={routes["docs.folder"].component}
                      abilities={routes["docs.folder"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["docs.file"].path}
                  element={
                    <ProtectedRoute
                      path={routes["docs.file"].path}
                      component={routes["docs.file"].component}
                      abilities={routes["docs.file"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["statistics.general"].path}
                  element={
                    <ProtectedRoute
                      path={routes["statistics.general"].path}
                      component={routes["statistics.general"].component}
                      abilities={routes["statistics.general"].abilities}
                    />
                  }
                />
                <Route
                  path={routes["statistics.certifications"].path}
                  element={
                    <ProtectedRoute
                      path={routes["statistics.certifications"].path}
                      component={routes["statistics.certifications"].component}
                      abilities={routes["statistics.certifications"].abilities}
                    />
                  }
                />
                <Route
                  path={routes.settings.path}
                  element={
                    <ProtectedRoute
                      path={routes.settings.path}
                      component={routes.settings.component}
                      abilities={routes.settings.abilities}
                    />
                  }
                />

                <Route
                  path={routes.root.path}
                  element={<routes.root.component />}
                />
              </Routes>
              {state.loading.length > 0 && <VoLoader />}
            </AuthGuard>
          </VoAppContext.Provider>
        </ErrorBoundary>
      </React.Suspense>
    </BrowserRouter>
  );
}

export default App;
